import { useParams, useNavigate } from 'react-router-dom';
import { useEquipo } from '../context/EquipoContext';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';

export default function PerfilPublico() {
  const { slug }     = useParams();
  const { equipo }   = useEquipo();
  const { user }     = useAuth();
  const navigate     = useNavigate();

  const [datos,       setDatos]       = useState(null);
  const [jugadores,   setJugadores]   = useState([]);
  const [reputacion,  setReputacion]  = useState({ puntualidad: 0, fairplay: 0, ambiente: 0, organizacion: 0 });
  const [cargando,    setCargando]    = useState(true);

  const API = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!slug) return;
    setCargando(true);

    // 1. Cargar equipo por slug
    fetch(`${API}/api/equipos/${slug}`)
      .then(r => r.json())
      .then(async equipoData => {
        if (equipoData.error) { setCargando(false); return; }
        setDatos(equipoData);

        // 2. Cargar jugadores
        const jugsRes = await fetch(`${API}/api/jugadores/equipo/${equipoData.id}`);
        const jugs = await jugsRes.json();
        if (Array.isArray(jugs)) setJugadores(jugs);

        // 3. Cargar evaluaciones recibidas
        const evalRes = await fetch(`${API}/api/evaluaciones/equipo/${equipoData.id}`);
        const evals = await evalRes.json();
        if (Array.isArray(evals) && evals.length > 0) {
          const avg = (campo) => {
            const vals = evals.map(e => e[campo]).filter(v => v > 0);
            return vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
          };
          setReputacion({
            puntualidad:  avg('puntualidad'),
            fairplay:     avg('fairplay'),
            ambiente:     avg('ambiente'),
            organizacion: avg('organizacion'),
          });
        }
      })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, [slug]);

  if (cargando) {
    return (
      <div style={styles.pagina}>
        <div style={{ textAlign: 'center', padding: '80px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" }}>
          Cargando perfil...
        </div>
      </div>
    );
  }

  if (!datos) {
    return (
      <div style={styles.pagina}>
        <div style={{ textAlign: 'center', padding: '80px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" }}>
          Equipo no encontrado.
        </div>
      </div>
    );
  }

  const esPropio = equipo?.id === datos.id;

  const iniciales = datos.nombre
    ? datos.nombre.split(' ').filter(p => p.length > 0).slice(0,2).map(p => p[0].toUpperCase()).join('')
    : '??';

  const PALABRAS_GENERICAS = ['los','las','el','la','club','fc','cf','sd'];
  const nombreCorto = datos.nombre
    ? datos.nombre.split(' ').find(p => !PALABRAS_GENERICAS.includes(p.toLowerCase())) || datos.nombre.split(' ')[0]
    : 'este equipo';

  // Cálculos físicos desde jugadores reales
  const edades = jugadores.filter(j => j.edad && !isNaN(j.edad)).map(j => Number(j.edad));
  const pesos  = jugadores.filter(j => j.peso && !isNaN(j.peso)).map(j => Number(j.peso));

  const edadPromedio = edades.length > 0 ? Math.round(edades.reduce((a,b) => a+b,0) / edades.length) : '--';
  const edadMinima   = edades.length > 0 ? Math.min(...edades) : '--';
  const edadMaxima   = edades.length > 0 ? Math.max(...edades) : '--';
  const pesoPromedio = pesos.length > 0  ? Math.round(pesos.reduce((a,b) => a+b,0) / pesos.length)  : null;

  // Promedio general de reputación
  const promedioRep = datos.partidos > 0
    ? ((reputacion.puntualidad + reputacion.fairplay + reputacion.ambiente + reputacion.organizacion) / 4).toFixed(1)
    : null;

  const handleDesafiar = () => {
    if (user) {
      navigate('/app/matchmaking', { state: { rivalSlug: slug } });
    } else {
      navigate('/registro');
    }
  };

  const handleCopiarLink = () => {
    navigator.clipboard.writeText(`turival.cl/equipo/${datos.url_nombre}`);
    alert('¡Link copiado!');
  };

  const modalidades = Array.isArray(datos.modalidades)
    ? datos.modalidades.map(m =>
        m === 'futbol5' ? 'Fútbol 5' :
        m === 'futbol7' ? 'Fútbol 7' :
        m === 'futbol11' ? 'Fútbol 11' :
        m === 'padel' ? 'Pádel' : m
      ).join(' · ')
    : '';

  return (
    <div style={styles.pagina}>
      <div style={styles.container}>

        {/* HEADER */}
        <div style={styles.header}>
          <div style={styles.headerInner}>
            <div style={{
              ...styles.escudo,
              background: `linear-gradient(135deg, ${datos.color_principal || '#1A6BFF'}, ${datos.color_secundario || '#FF6B1A'})`,
            }}>
              {iniciales}
            </div>
            <div style={styles.headerInfo}>
              <div style={styles.nombreEquipo}>{datos.nombre}</div>
              <div style={styles.headerMeta}>
                {datos.zona} · {modalidades}
              </div>
              <div style={styles.badges}>
                <span style={styles.badgeVerificado}>✓ Verificado</span>
                {promedioRep && (
                  <span style={{ ...styles.badgeVerificado, background: 'rgba(255,107,26,0.2)', color: '#FF6B1A' }}>
                    ⭐ {promedioRep} reputación
                  </span>
                )}
              </div>
            </div>
            <div style={styles.eloWrap}>
              <div style={styles.eloNum}>{datos.elo || 800}</div>
              <div style={styles.eloLabel}>ELO TuRival</div>
            </div>
          </div>

          <div style={styles.statsRow}>
            {[
                { n: datos.partidos  || 0,                                                   l: 'Partidos'  },
                { n: datos.victorias || 0,                                                   l: 'Victorias' },
                { n: datos.empates   || 0,                                                   l: 'Empates'   },
                { n: (datos.partidos||0)-(datos.victorias||0)-(datos.empates||0),            l: 'Derrotas'  },
                { n: jugadores.length,                                                       l: 'Jugadores' },
              ].map(({ n, l }) => (
              <div key={l} style={styles.statBox}>
                <div style={styles.statNum}>{n}</div>
                <div style={styles.statLabel}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* DOS COLUMNAS */}
        <div style={styles.cols}>
          <div style={styles.colLeft}>

            {/* Perfil físico */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Perfil físico del equipo</div>
              {jugadores.length === 0 ? (
                <div style={styles.sinDatos}>Sin jugadores registrados aún.</div>
              ) : (
                <>
                  <div style={styles.fisicaGrid}>
                    <div style={styles.fisicaBox}>
                      <div style={styles.fisicaNum}>{edadPromedio}</div>
                      <div style={styles.fisicaLabel}>Edad promedio</div>
                    </div>
                    <div style={styles.fisicaBox}>
                      <div style={{ ...styles.fisicaNum, color: '#16A34A' }}>{edadMinima}</div>
                      <div style={styles.fisicaLabel}>Más joven</div>
                    </div>
                    <div style={styles.fisicaBox}>
                      <div style={{ ...styles.fisicaNum, color: '#FF6B1A' }}>{edadMaxima}</div>
                      <div style={styles.fisicaLabel}>Más veterano</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 10, background: 'rgba(26,107,255,0.08)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                      Peso promedio del plantel
                    </span>
                    <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 800, color: '#1A6BFF' }}>
                      {pesoPromedio ? `${pesoPromedio} kg` : '--'}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Evaluaciones */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>
                Evaluaciones de rivales
                {promedioRep && <span style={{ color: '#FF6B1A', marginLeft: 8 }}>· Promedio {promedioRep}</span>}
              </div>
              {datos.partidos === 0 ? (
                <div style={styles.sinDatos}>
                  Sin partidos jugados aún. Las evaluaciones aparecerán después del primer encuentro.
                </div>
              ) : (
                [
                  { label: 'Puntualidad',   val: reputacion.puntualidad,  color: '#1A6BFF' },
                  { label: 'Fair play',     val: reputacion.fairplay,     color: '#1A6BFF' },
                  { label: 'Buen ambiente', val: reputacion.ambiente,     color: '#FF6B1A' },
                  { label: 'Organización',  val: reputacion.organizacion, color: '#FF6B1A' },
                ].map(({ label, val, color }) => (
                  <div key={label} style={styles.evalRow}>
                    <span style={styles.evalLabel}>{label}</span>
                    <div style={styles.evalBar}>
                      <div style={{ ...styles.evalFill, width: `${val * 10}%`, background: color }} />
                    </div>
                    <span style={styles.evalScore}>{val.toFixed(1)}</span>
                  </div>
                ))
              )}
            </div>

            {/* Disponibilidad */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Disponibilidad</div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Días</span>
                <div style={styles.tagGroup}>
                  {(datos.dias || []).length > 0
                    ? datos.dias.map(d => <span key={d} style={styles.tag}>{d}</span>)
                    : <span style={styles.sinDatos}>No especificados</span>}
                </div>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Horarios</span>
                <div style={styles.tagGroup}>
                  {(datos.horarios || []).length > 0
                    ? datos.horarios.map(h => (
                        <span key={h} style={styles.tag}>
                          {h === 'manana' ? 'Mañana' : h === 'tarde' ? 'Tarde' : 'Noche'}
                        </span>
                      ))
                    : <span style={styles.sinDatos}>No especificados</span>}
                </div>
              </div>
              <div style={{ ...styles.infoRow, borderBottom: 'none' }}>
                <span style={styles.infoLabel}>Zona</span>
                <span style={styles.infoVal}>{datos.zona} · {datos.region}</span>
              </div>
            </div>

          </div>

          <div style={styles.colRight}>

            {/* CTA */}
            <div style={styles.card}>
              {esPropio ? (
                <div style={styles.esPropio}>Este es tu equipo</div>
              ) : (
                <button style={styles.btnDesafiar} onClick={handleDesafiar}>
                  ⚡ Desafiar a {nombreCorto}
                </button>
              )}
              <div style={styles.cardTitle}>Compartir en RRSS</div>
              <div style={styles.tarjetaRRSS}>
                <div style={{
                  ...styles.tarjetaEscudo,
                  background: `linear-gradient(135deg, ${datos.color_principal || '#1A6BFF'}, ${datos.color_secundario || '#FF6B1A'})`,
                }}>
                  {iniciales}
                </div>
                <div>
                  <div style={styles.tarjetaNombre}>{datos.nombre}</div>
                  <div style={styles.tarjetaMeta}>ELO {datos.elo || 800} · {datos.zona}</div>
                  <div style={styles.tarjetaUrl}>turival.cl/equipo/{datos.url_nombre}</div>
                </div>
              </div>
              <button style={styles.btnCompartir} onClick={handleCopiarLink}>
                Copiar link del equipo
              </button>
            </div>

            {/* Nómina */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Nómina · {jugadores.length} jugadores</div>
              {jugadores.length === 0 ? (
                <div style={styles.sinDatos}>Sin jugadores registrados.</div>
              ) : (
                jugadores.slice(0, 8).map((j, i) => (
                  <div key={j.id || i} style={styles.jugadorRow}>
                    <div style={styles.jugadorNum}>{i + 1}</div>
                    <div style={styles.jugadorInfo}>
                      <div style={styles.jugadorNombre}>{j.nombre}</div>
                      <div style={styles.jugadorMeta}>
                        {j.edad ? `${j.edad} años` : ''}
                        {j.edad && j.peso ? ' · ' : ''}
                        {j.peso ? `${j.peso} kg` : ''}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {jugadores.length > 8 && (
                <div style={styles.verMas}>+{jugadores.length - 8} jugadores más</div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pagina: { minHeight: '100vh', background: '#0D1117', padding: '32px 20px' },
  container: { maxWidth: 900, margin: '0 auto' },
  header: { background: '#161C26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '24px', marginBottom: 16 },
  headerInner: { display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 20 },
  escudo: { width: 72, height: 72, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 24, fontWeight: 800, color: 'white', flexShrink: 0, border: '2px solid rgba(255,255,255,0.15)' },
  headerInfo: { flex: 1 },
  nombreEquipo: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 28, fontWeight: 800, color: 'white', marginBottom: 4 },
  headerMeta: { fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 10, fontFamily: "'Barlow', sans-serif" },
  badges: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  badgeVerificado: { background: 'rgba(22,163,74,0.2)', color: '#4ade80', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
  eloWrap: { textAlign: 'right', flexShrink: 0 },
  eloNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 40, fontWeight: 800, color: 'white', lineHeight: 1 },
  eloLabel: { fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 },
  statBox: { background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px', textAlign: 'center' },
  statNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 800, color: 'white', lineHeight: 1, marginBottom: 3 },
  statLabel: { fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'Barlow', sans-serif" },
  cols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' },
  colLeft: { display: 'flex', flexDirection: 'column', gap: 16 },
  colRight: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: '#161C26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '18px' },
  cardTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 },
  fisicaGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 },
  fisicaBox: { background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '12px 8px', textAlign: 'center' },
  fisicaNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 24, fontWeight: 800, color: 'white', lineHeight: 1, marginBottom: 4 },
  fisicaLabel: { fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: "'Barlow', sans-serif", lineHeight: 1.3 },
  sinDatos: { fontSize: 13, color: 'rgba(255,255,255,0.25)', fontFamily: "'Barlow', sans-serif", fontStyle: 'italic', lineHeight: 1.5 },
  evalRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  evalLabel: { fontSize: 12, color: 'rgba(255,255,255,0.5)', minWidth: 100, fontFamily: "'Barlow', sans-serif" },
  evalBar: { flex: 1, height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' },
  evalFill: { height: '100%', borderRadius: 3, transition: 'width 0.3s' },
  evalScore: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, color: 'white', minWidth: 26, textAlign: 'right' },
  infoRow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  infoLabel: { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 70, paddingTop: 2, fontFamily: "'Barlow', sans-serif" },
  infoVal: { fontSize: 13, color: 'rgba(255,255,255,0.7)', fontFamily: "'Barlow', sans-serif" },
  tagGroup: { display: 'flex', gap: 5, flexWrap: 'wrap' },
  tag: { background: 'rgba(26,107,255,0.2)', color: '#60a5fa', fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
  esPropio: { background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: '13px', textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.4)', marginBottom: 16 },
  btnDesafiar: { width: '100%', padding: '14px', background: '#FF6B1A', border: 'none', borderRadius: 12, fontFamily: "'Barlow Condensed', sans-serif", fontSize: 18, fontWeight: 800, color: 'white', cursor: 'pointer', marginBottom: 20, letterSpacing: '0.02em' },
  tarjetaRRSS: { display: 'flex', alignItems: 'center', gap: 12, background: '#0D1117', borderRadius: 12, padding: '14px', marginBottom: 10, border: '1px solid rgba(255,255,255,0.06)' },
  tarjetaEscudo: { width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 800, color: 'white', flexShrink: 0 },
  tarjetaNombre: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 800, color: 'white', marginBottom: 2 },
  tarjetaMeta: { fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif", marginBottom: 2 },
  tarjetaUrl: { fontSize: 11, color: '#1A6BFF', fontFamily: "'Barlow', sans-serif" },
  btnCompartir: { width: '100%', padding: '10px', background: 'transparent', border: '1.5px solid rgba(255,255,255,0.15)', borderRadius: 10, fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.6)', cursor: 'pointer' },
  jugadorRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  jugadorNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.2)', minWidth: 20, textAlign: 'center' },
  jugadorInfo: { flex: 1 },
  jugadorNombre: { fontSize: 13, fontWeight: 600, color: 'white', fontFamily: "'Barlow', sans-serif" },
  jugadorMeta: { fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow', sans-serif" },
  verMas: { fontSize: 12, color: '#1A6BFF', fontFamily: "'Barlow', sans-serif", textAlign: 'center', paddingTop: 10, cursor: 'pointer' },
};